import { CaretUpOutlined } from '@ant-design/icons';
import {
  ApolloCache,
  NormalizedCacheObject,
  useLazyQuery,
  useMutation,
} from '@apollo/client';
import {
  GET_CHANNEL_IDEA,
  GET_IDEA,
  Idea,
  IDEA_LIKE,
  IDEA_UNLIKE,
} from '@frontend/data-access';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { DiscussionWall } from '@frontend/feature-discussion';
import { Applicants } from '@frontend/feature-idea-channel';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { IdeaContributionList } from '@frontend/shared/ui';
import { Divider, Tabs } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import React, { useEffect, useState } from 'react';
import IdeaActivityLog from '../idea-activity-log/idea-activity-log';
import IdeaDetailAdditionalAttachments from './additional-attachments';
import IdeaDetailAttachments from './attachments';
import IdeaDetailBody from './body';
import IdeaDetailMetrics from './metrics';
import IdeaQuestionnaire from './answers';

function IdeaContents({
  ideaId,
  printMode,
}: {
  ideaId: string;
  printMode?: boolean;
}) {
  const { t: tcommon } = useTranslation('common');
  const [isLiked, setIsLiked] = useState<boolean>(false);
  const [likeCount, setLikeCount] = useState<number>(0);

  const [getIdea, { data }] = useLazyQuery<{ channelIdea: Idea }>(
    GET_CHANNEL_IDEA
  );
  const applicants = [
    ...(data?.channelIdea.createdBy ? [data?.channelIdea.createdBy] : []),
    ...(data?.channelIdea.applicants
      ?.filter(
        (i) =>
          i.status === 'APPROVED' &&
          i.userId !== data?.channelIdea.createdBy?.id
      )
      .map((e) => e.user) || []),
  ];

  const handleUpdateCacheLike = (cache: ApolloCache<NormalizedCacheObject>) => {
    const cacheData: { idea: Idea } | null = cache.readQuery({
      query: GET_IDEA,
      variables: {
        ideaId: data?.channelIdea.id,
      },
    });

    if (cacheData?.idea) {
      cache.writeQuery({
        query: GET_IDEA,
        variables: {
          ideaId: data?.channelIdea.id,
        },
        data: {
          idea: {
            ...cacheData?.idea,
            isLiked: true,
            likes: cacheData?.idea.likes + 1,
          },
        },
      });
    }
  };

  const handleUpdateCacheUnLike = (
    cache: ApolloCache<NormalizedCacheObject>
  ) => {
    const cacheData: { idea: Idea } | null = cache.readQuery({
      query: GET_IDEA,
      variables: {
        ideaId: data?.channelIdea.id,
      },
    });

    if (cacheData?.idea) {
      cache.writeQuery({
        query: GET_IDEA,
        variables: {
          ideaId: data?.channelIdea.id,
        },
        data: {
          idea: {
            ...cacheData?.idea,
            isLiked: false,
            likes: cacheData?.idea.likes - 1,
          },
        },
      });
      setIsLiked(true);
      setLikeCount(cacheData?.idea.likes - 1);
    }
  };

  const [likeIdea] = useMutation(IDEA_LIKE, {
    update: handleUpdateCacheLike,
    onCompleted() {
      setIsLiked(true);
      setLikeCount(likeCount + 1);
    },
  });
  const [unlikeIdea] = useMutation(IDEA_UNLIKE, {
    update: handleUpdateCacheUnLike,
    onCompleted() {
      setIsLiked(false);
      setLikeCount(likeCount - 1);
    },
  });

  useEffect(() => {
    if (ideaId) {
      getIdea({ variables: { ideaId: ideaId } });
    }
  }, [getIdea, ideaId]);

  useEffect(() => {
    if (data?.channelIdea) {
      setIsLiked(data.channelIdea.isLiked);
      setLikeCount(data.channelIdea.likes);
    }
  }, [data]);

  const handleLike = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (isLiked) {
      unlikeIdea({
        variables: {
          id: data?.channelIdea.id,
        },
      });
    } else {
      likeIdea({
        variables: {
          id: data?.channelIdea.id,
        },
      });
    }
  };

  const items = [];

  if (data?.channelIdea?.internalDiscussion?.id) {
    items.push({
      key: 'evaluation-comments',
      label:
        data?.channelIdea?.internalComments === 0
          ? tcommon('idea_detail_evaluation_comments')
          : `${tcommon('idea_detail_evaluation_comments')} (${
              data?.channelIdea.internalComments
            })`,
      children: (
        <>
          <div className="text-xs">
            {tcommon('idea_detail_evaluation_comment_description')}
          </div>
          <DiscussionWall
            id={data?.channelIdea?.internalDiscussion?.id}
            title=" "
          />
        </>
      ),
    });
  }

  if (data?.channelIdea?.discussion?.id) {
    items.push({
      key: 'public-comments',
      label:
        data?.channelIdea?.comments === 0
          ? tcommon('public_comments')
          : `${tcommon('public_comments')} (${data?.channelIdea.comments})`,
      children: (
        <DiscussionWall id={data?.channelIdea?.discussion?.id} title=" " />
      ),
    });
  }
  if (data?.channelIdea.regContribute) {
    items.push({
      key: 'contribution',
      label:
        data?.channelIdea.contributeCount === 0
          ? tcommon('idea_detail_contributions')
          : `${tcommon('idea_detail_contributions')} (${
              data?.channelIdea.contributeCount
            })`,
      children: (
        <IdeaContributionList
          title=" "
          ideaId={data?.channelIdea.id}
          contributionCount={data?.channelIdea.contributeCount}
        />
      ),
    });
  }

  if (data?.channelIdea.id) {
    items.push({
      key: 'activities',
      label: tcommon('activity_logs'),
      children: <IdeaActivityLog ideaId={data.channelIdea.id} />,
    });
  }

  if (data?.channelIdea)
    return (
      <div className="space-y-5 pr-5">
        <div className="space-y-2">
          <div className="text-gray-700 text-sm font-normal">
            {tcommon('applicants')}
          </div>
          <div className="flex flex-wrap justify-between">
            <Applicants
              users={applicants}
              anonymous={data.channelIdea.anonymous || false}
            />
            <div
              className={`px-2 py-1 rounded-full border border-gray-400 flex flex-col gap-0 items-center justify-center select-none cursor-pointer ${
                isLiked
                  ? 'bg-primary-600 text-white'
                  : 'hover:border-primary-600 hover:text-primary-600'
              }`}
              onClick={handleLike}
            >
              <CaretUpOutlined />
              <span className="text-xs">{likeCount}</span>
            </div>
          </div>
        </div>
        <IdeaDetailBody idea={data.channelIdea} />
        <Divider />
        <IdeaQuestionnaire
          idea={data.channelIdea}
          printMode={printMode || false}
        />
        <Divider />
        <IdeaDetailAttachments idea={data.channelIdea} />
        <Divider />
        <IdeaDetailAdditionalAttachments idea={data.channelIdea} />
        <Divider />
        <IdeaDetailMetrics idea={data.channelIdea} enableManage />
        <Divider />
        <Tabs items={items} />
      </div>
    );
  else return <></>;
}

export default IdeaContents;
